'use client';

import type { ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@ecom/helper-react-class-combine';
import {
  setAuthIntentCookie,
  setAuthRedirectCookie,
  setNewsletterConsentCookie,
} from '@/features/auth/internal/helpers/social-auth/auth-intent-cookie';
import { buildSocialAuthRedirectUrl } from '@/features/auth/shared/helpers/build-social-auth-redirect';
import { pagesRoutes } from '@/shared/constants/routes';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { useDesktopOAuth } from '@/features/auth/shared/hooks/use-desktop-oauth';
import { useSocialAuthLoading } from '@/features/auth/shared/hooks/use-social-auth-loading';
import { AccountProvider } from '@ecom/storefront-auth-db';
import { Button } from '@/shared/components/primitives/button';

type SocialProvider = Exclude<AccountProvider, AccountProvider.PASSWORD>;

type SocialAuthButtonProps = {
  buttonText: string;
  intent: 'login' | 'signup';
  provider: SocialProvider;
  icon: ReactElement;
  callbackUrl?: string;
  returnTo?: string;
  className?: string;
  buttonClassName: string;
  onStart?: () => void;
  onWebSignIn: (redirectTo: string) => Promise<void>;
  subscribeToNewsletter?: boolean;
  onInClientSuccess?: () => void;
};

export const SocialAuthButton = ({
  buttonText,
  intent,
  provider,
  icon,
  callbackUrl,
  returnTo,
  className,
  buttonClassName,
  onStart,
  onWebSignIn,
  subscribeToNewsletter = true,
  onInClientSuccess,
}: SocialAuthButtonProps): ReactElement => {
  const isGamesApp = useIsWTGamesApp();
  const { triggerOAuth, cancelOAuth, isAuthenticating } = useDesktopOAuth({
    onSuccess: onInClientSuccess,
  });
  const { isLoading: isWebLoading, startLoading: startWebLoading } = useSocialAuthLoading();
  const t = useTranslations('Auth.login');

  const showAuthenticating = isGamesApp ? isAuthenticating : isWebLoading;
  const isDisabled = showAuthenticating;

  const handleSocialAuth = async (): Promise<void> => {
    if (isDisabled) return;
    onStart?.();
    if (isGamesApp) {
      cancelOAuth();
      await triggerOAuth({
        provider,
        intent,
        newsletterConsent: intent === 'signup' ? subscribeToNewsletter : undefined,
      });
    } else {
      startWebLoading();
      setAuthIntentCookie(intent);
      if (intent === 'signup') {
        setNewsletterConsentCookie(subscribeToNewsletter);
      }
      setAuthRedirectCookie(returnTo);
      const redirectTo = buildSocialAuthRedirectUrl(
        callbackUrl || pagesRoutes.home.href,
        intent,
        provider,
      );
      await onWebSignIn(redirectTo);
    }
  };

  return (
    <div className={cn('flex w-full flex-col items-center', className)}>
      <Button
        type='button'
        onClick={handleSocialAuth}
        disabled={isDisabled}
        variant='primary'
        size='md'
        className={buttonClassName}
      >
        <span className='inline-flex items-center gap-4'>
          {icon}
          {showAuthenticating ? t('authenticating') : buttonText}
        </span>
      </Button>
    </div>
  );
};
